/**
 * useRenderQueuePersistence
 *
 * Persists the render queue to the workspace filesystem per-project
 * (`projects/<projectId>/render-queue.json`).
 *
 * Design:
 * - Debounces saves (coalesced via queueMicrotask) so rapid progress
 *   updates don't thrash the disk.
 * - After a job reaches a terminal status (completed/failed/cancelled),
 *   the save fires on the very next microtask to ensure it lands on disk
 *   before a refresh can lose it.
 * - On hydration: resets in-flight jobs back to `queued` and pauses the
 *   queue so a page refresh never silently resumes renders.
 */

import { useEffect, useRef } from 'react'
import { useRenderQueueStore } from '../stores/render-queue-store'
import { useProjectStore } from '@/features/export/deps/projects'
import {
  requireWorkspaceRoot,
} from '@/infrastructure/storage/workspace-fs/root'
import {
  writeJsonAtomic,
} from '@/infrastructure/storage/workspace-fs/fs-primitives'
import {
  projectRenderQueuePath,
} from '@/infrastructure/storage/workspace-fs/paths'
import { readJson } from '@/infrastructure/storage/workspace-fs/fs-primitives'
import { createLogger } from '@/shared/logging/logger'
import type { RenderJob } from '../types/render-queue'

const log = createLogger('RenderQueuePersistence')

/** Coalesced save handle — set to non-null when a microtask save is pending. */
let pendingSaveHandle: (() => void) | null = null

/**
 * RenderQueueState stored on disk — omits runtime-only fields.
 */
interface PersistedRenderQueueState {
  jobs: RenderJob[]
  isPaused: boolean
  updatedAt?: number
}

const RENDER_QUEUE_CACHE_PREFIX = 'freecut:render-queue:'

function getRenderQueueCacheKey(projectId: string): string {
  return `${RENDER_QUEUE_CACHE_PREFIX}${projectId}`
}

function readRenderQueueCache(projectId: string): PersistedRenderQueueState | null {
  if (typeof localStorage === 'undefined') return null
  try {
    const raw = localStorage.getItem(getRenderQueueCacheKey(projectId))
    if (!raw) return null
    return JSON.parse(raw) as PersistedRenderQueueState
  } catch (error) {
    log.warn('Failed to read render queue cache', { projectId, error })
    return null
  }
}

function writeRenderQueueCache(projectId: string, state: PersistedRenderQueueState): void {
  if (typeof localStorage === 'undefined') return
  try {
    localStorage.setItem(getRenderQueueCacheKey(projectId), JSON.stringify(state))
  } catch (error) {
    log.warn('Failed to write render queue cache', { projectId, error })
  }
}

function normalizePersistedJob(job: RenderJob): RenderJob {
  const snapshot = job?.snapshot ?? ({} as RenderJob['snapshot'])
  const rawSettings = snapshot.settings
  const resolution = rawSettings?.resolution ?? {
    width: snapshot.projectWidth ?? 1920,
    height: snapshot.projectHeight ?? 1080,
  }

  return {
    ...job,
    progress: typeof job?.progress === 'number' ? job.progress : 0,
    createdAt: typeof job?.createdAt === 'number' ? job.createdAt : Date.now(),
    snapshot: {
      tracks: snapshot.tracks ?? [],
      items: snapshot.items ?? [],
      transitions: snapshot.transitions ?? [],
      fps: snapshot.fps ?? 30,
      inPoint: snapshot.inPoint ?? null,
      outPoint: snapshot.outPoint ?? null,
      keyframes: snapshot.keyframes ?? [],
      busAudioEq: snapshot.busAudioEq ?? null,
      masterBusDb: snapshot.masterBusDb ?? 0,
      backgroundColor: snapshot.backgroundColor ?? '#000000',
      projectWidth: snapshot.projectWidth ?? resolution.width ?? 1920,
      projectHeight: snapshot.projectHeight ?? resolution.height ?? 1080,
      settings: rawSettings ?? {
        codec: 'h264',
        quality: 'high',
        resolution,
      },
    },
  }
}

/**
 * Schedule a save on the next microtask, coalescing multiple calls.
 * Uses a ref-getter so the microtask always picks up the latest state.
 */
function scheduleSave(
  projectId: string,
  getState: () => PersistedRenderQueueState,
): void {
  const saveToken = () => {}
  pendingSaveHandle = saveToken
  queueMicrotask(async () => {
    if (pendingSaveHandle !== saveToken) return
    pendingSaveHandle = null
    try {
      const root = requireWorkspaceRoot()
      const state = getState()
      await writeJsonAtomic(root, projectRenderQueuePath(projectId), state)
    } catch (error) {
      log.error('Failed to persist render queue', { projectId, error })
    }
  })
}

function normalizeLoadedQueueState(data: PersistedRenderQueueState): PersistedRenderQueueState {
  // Reset in-flight jobs back to queued so a refresh never
  // silently resumes a render in progress.
  const resetJobs: RenderJob[] = (data.jobs ?? []).map((job) => {
    const normalizedJob = normalizePersistedJob(job)
    if (job.status === 'rendering') {
      return {
        ...normalizedJob,
        status: 'queued' as const,
        progress: 0,
        result: undefined,
        error: undefined,
      }
    }
    return normalizedJob
  })

  return {
    jobs: resetJobs,
    isPaused: resetJobs.some((j) => j.status === 'queued') ? true : (data.isPaused ?? false),
    updatedAt: data.updatedAt ?? 0,
  }
}

/**
 * Load the render queue from disk for a given project.
 * Returns the persisted state (or null if none exists).
 */
export async function loadRenderQueue(projectId: string): Promise<PersistedRenderQueueState | null> {
  try {
    const cached = readRenderQueueCache(projectId)
    const root = requireWorkspaceRoot()
    const disk = await readJson<PersistedRenderQueueState>(root, projectRenderQueuePath(projectId))
    const latest =
      (cached?.updatedAt ?? 0) >= (disk?.updatedAt ?? 0)
        ? (cached ?? disk)
        : (disk ?? cached)

    if (!latest) return null
    return normalizeLoadedQueueState(latest)
  } catch (error) {
    log.error('Failed to load render queue', { projectId, error })
    return null
  }
}

/**
 * Load the render queue and hydrate the store.
 * This is the main entry point for restoring queue state on project load.
 */
export async function loadAndHydrateRenderQueue(projectId: string): Promise<void> {
  const persisted = await loadRenderQueue(projectId)
  if (persisted) {
    useRenderQueueStore.getState().hydrate({
      jobs: persisted.jobs,
      isPaused: persisted.isPaused,
    })
    writeRenderQueueCache(projectId, persisted)
    log.info(`Hydrated render queue for project ${projectId}: ${persisted.jobs.length} jobs`)
  } else {
    useRenderQueueStore.getState().reset()
  }
}

/**
 * Hook that subscribes to the render queue store and persists changes.
 * Mount once at the editor root alongside the runner.
 */
export function useRenderQueuePersistence(): void {
  const jobs = useRenderQueueStore((s) => s.jobs)
  const isPaused = useRenderQueueStore((s) => s.isPaused)
  const currentProjectId = useProjectStore((s) => s.currentProject?.id)

  // Track which snapshot to persist — use a ref so the microtask sees the latest data.
  const snapshotRef = useRef<PersistedRenderQueueState>({ jobs: [], isPaused: false, updatedAt: 0 })
  snapshotRef.current = { jobs, isPaused, updatedAt: Date.now() }

  // Persist whenever state changes
  useEffect(() => {
    if (!currentProjectId) return
    writeRenderQueueCache(currentProjectId, snapshotRef.current)
    scheduleSave(currentProjectId, () => snapshotRef.current)
  }, [jobs, isPaused, currentProjectId])

  // Hydrate on project load
  useEffect(() => {
    if (!currentProjectId) return

    const hydrate = async () => {
      await loadAndHydrateRenderQueue(currentProjectId)
    }

    hydrate()
  }, [currentProjectId])
}
